/**
 * Welle 9 (pre89b): Erzeugt das EC-Karten-Protokoll als PDF (statt Text), abhängigkeitsfrei
 * im Browser. Call-ID + Zeitstempel stehen sichtbar im Dokument UND in den PDF-Metadaten
 * (Title/Subject/Keywords als UTF-16BE, CreationDate). Sichtbarer Text nutzt Helvetica mit
 * WinAnsi-Encoding, damit Umlaute korrekt erscheinen.
 */

const CP1252_EXTRA: Record<number, number> = {
  0x20ac: 0x80, 0x201a: 0x82, 0x0192: 0x83, 0x201e: 0x84, 0x2026: 0x85, 0x2020: 0x86,
  0x2021: 0x87, 0x02c6: 0x88, 0x2030: 0x89, 0x0160: 0x8a, 0x2039: 0x8b, 0x0152: 0x8c,
  0x017d: 0x8e, 0x2018: 0x91, 0x2019: 0x92, 0x201c: 0x93, 0x201d: 0x94, 0x2022: 0x95,
  0x2013: 0x96, 0x2014: 0x97, 0x02dc: 0x98, 0x2122: 0x99, 0x0161: 0x9a, 0x203a: 0x9b,
  0x0153: 0x9c, 0x017e: 0x9e, 0x0178: 0x9f,
};
function winByte(cp: number): number {
  if (cp >= 0x20 && cp <= 0x7e) return cp;
  if (cp >= 0xa0 && cp <= 0xff) return cp;
  if (CP1252_EXTRA[cp] !== undefined) return CP1252_EXTRA[cp];
  return 0x3f; // '?'
}
function asciiBytes(s: string): Uint8Array {
  const o = new Uint8Array(s.length);
  for (let i = 0; i < s.length; i++) o[i] = s.charCodeAt(i) & 0xff;
  return o;
}
function pdfStr(s: string): Uint8Array {
  const o: number[] = [0x28];
  for (const ch of s) {
    const b = winByte(ch.codePointAt(0) as number);
    if (b === 0x28 || b === 0x29 || b === 0x5c) o.push(0x5c);
    o.push(b);
  }
  o.push(0x29);
  return Uint8Array.from(o);
}
function pdfHexUTF16(s: string): string {
  let hex = 'FEFF';
  for (const ch of s) {
    const cp = ch.codePointAt(0) as number;
    if (cp > 0xffff) {
      const v = cp - 0x10000;
      hex += (0xd800 + (v >> 10)).toString(16).padStart(4, '0') + (0xdc00 + (v & 0x3ff)).toString(16).padStart(4, '0');
    } else {
      hex += cp.toString(16).padStart(4, '0');
    }
  }
  return '<' + hex.toUpperCase() + '>';
}
function concat(arrs: Uint8Array[]): Uint8Array {
  let n = 0;
  for (const a of arrs) n += a.length;
  const o = new Uint8Array(n);
  let p = 0;
  for (const a of arrs) { o.set(a, p); p += a.length; }
  return o;
}

export function buildEcProtocolPdf(fields: Record<string, string>, opts: { callId: string; at: number }): Blob {
  const { callId, at } = opts;
  const d = new Date(at);
  const p2 = (n: number) => String(n).padStart(2, '0');
  const iso = d.toISOString();
  const human = d.toLocaleString('de-DE');
  const pdfDate = `D:${d.getFullYear()}${p2(d.getMonth() + 1)}${p2(d.getDate())}${p2(d.getHours())}${p2(d.getMinutes())}${p2(d.getSeconds())}`;

  // Content-Stream
  const c: Uint8Array[] = [];
  const push = (x: string | Uint8Array) => c.push(typeof x === 'string' ? asciiBytes(x) : x);
  push('BT\n');
  push('/F2 18 Tf\n1 0 0 1 56 790 Tm\n'); push(pdfStr('EC-Karten-Protokoll')); push(' Tj\n');
  let y = 760;
  const line = (text: string, font = 'F1', size = 11, gap = 16) => {
    push(`/${font} ${size} Tf\n1 0 0 1 56 ${y} Tm\n`); push(pdfStr(text)); push(' Tj\n'); y -= gap;
  };
  line(`Call-ID: ${callId}`, 'F2', 11, 15);
  line(`Erfasst: ${human}`, 'F1', 11, 22);
  for (const [k, v] of Object.entries(fields)) line(`${k}: ${v}`, 'F1', 12, 18);
  y -= 10; line('Lokal im Browser erfasst · Bamoa Videochat', 'F1', 9, 14);
  push('ET\n');
  const content = concat(c);

  // Objekte
  const objs: Uint8Array[] = [];
  objs[1] = asciiBytes('<</Type/Catalog/Pages 2 0 R>>');
  objs[2] = asciiBytes('<</Type/Pages/Kids[3 0 R]/Count 1>>');
  objs[3] = asciiBytes('<</Type/Page/Parent 2 0 R/MediaBox[0 0 595 842]/Resources<</Font<</F1 5 0 R/F2 6 0 R>>>>/Contents 4 0 R>>');
  objs[4] = concat([asciiBytes(`<</Length ${content.length}>>\nstream\n`), content, asciiBytes('\nendstream')]);
  objs[5] = asciiBytes('<</Type/Font/Subtype/Type1/BaseFont/Helvetica/Encoding/WinAnsiEncoding>>');
  objs[6] = asciiBytes('<</Type/Font/Subtype/Type1/BaseFont/Helvetica-Bold/Encoding/WinAnsiEncoding>>');
  objs[7] = asciiBytes(
    `<</Title${pdfHexUTF16(`EC-Karten-Protokoll – Call ${callId}`)}` +
    `/Subject${pdfHexUTF16(`Call-ID: ${callId} · Erfasst: ${iso}`)}` +
    `/Keywords${pdfHexUTF16(`Call-ID=${callId}; Zeitstempel=${iso}`)}` +
    `/Author${pdfHexUTF16('Bamoa Videochat')}` +
    `/Creator${pdfHexUTF16('Bamoa Videochat')}` +
    `/CreationDate(${pdfDate})/ModDate(${pdfDate})>>`,
  );

  // Datei mit Offsets
  const parts: Uint8Array[] = [];
  let off = 0;
  const xref: number[] = [];
  const add = (b: Uint8Array) => { parts.push(b); off += b.length; };
  add(asciiBytes('%PDF-1.4\n%\xE2\xE3\xCF\xD3\n'));
  for (let i = 1; i <= 7; i++) { xref[i] = off; add(asciiBytes(`${i} 0 obj\n`)); add(objs[i]); add(asciiBytes('\nendobj\n')); }
  const xrefOff = off;
  let xr = `xref\n0 8\n0000000000 65535 f \n`;
  for (let i = 1; i <= 7; i++) xr += `${String(xref[i]).padStart(10, '0')} 00000 n \n`;
  add(asciiBytes(xr));
  add(asciiBytes(`trailer\n<</Size 8/Root 1 0 R/Info 7 0 R>>\nstartxref\n${xrefOff}\n%%EOF\n`));

  return new Blob([concat(parts) as unknown as BlobPart], { type: 'application/pdf' });
}
